import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Form, FormGroup, Spinner } from 'reactstrap';
import { CheckInputField, TextInputField } from '../../components/Forms/fields';
import TurnstileWidget from '../../components/TurnstileWidget';
import { getErrorMessage } from '../../ajax/errors';
import { postSurveySubmitAsync } from '../../ajax/requests';
import { resolveSurveyDescription } from '../../shared/surveyDescription';
import { SurveyFieldDefinitionData, SurveyFieldType, SurveyTokenData } from '../../shared/surveys';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import {
    AnonymousDescription,
    AnonymousSection,
    AnonymousTitle,
    BannerAlert,
    CenterState,
    CenterStateInner,
    ChoiceButton,
    ChoiceIndicator,
    ChoiceList,
    CompanyHeader,
    CompanyLogo,
    CompanyName,
    ContentCard,
    ContentInner,
    FooterText,
    FormFooter,
    HeroBadge,
    HeroCard,
    HeroCopy,
    HeroInner,
    HeroMetaCard,
    HeroMetaGrid,
    HeroMetaLabel,
    HeroMetaValue,
    HeroSideCard,
    HeroSidePanel,
    LoadingText,
    MandatoryBadge,
    Page,
    PageShell,
    QuestionCard,
    QuestionHeader,
    QuestionHint,
    QuestionList,
    QuestionNumber,
    QuestionTitle,
    ScaleChoiceButton,
    ScaleChoiceGrid,
    ScaleFallback,
    ScaleLabel,
    ScaleValue,
    SectionText,
    SectionTitle,
    SideText,
    SideTitle,
    StyledSlider,
    SubmitButton,
    Subtitle,
    SuccessIcon,
    Title,
} from './SurveyFormView.styles';

export { Page, PageShell, HeroCard, CenterState, CenterStateInner, SuccessIcon, LoadingText };

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const getInitialValues = (data: SurveyTokenData): Record<string, string> => {
    if (data.existingValues) {
        return data.existingValues;
    }

    const initialValues: Record<string, string> = {};
    (data.survey?.fields ?? []).forEach((field, index) => {
        if (field.type === SurveyFieldType.Scale) {
            initialValues[index.toString()] = (field.scaleMin ?? 1).toString();
        }
    });

    return initialValues;
};

export const getMissingMandatoryFields = (
    fields: SurveyFieldDefinitionData[],
    values: Record<string, string>,
): string[] => {
    return fields
        .filter((field, index) => field.isMandatory && !values[index.toString()]?.trim() && !values[field.label]?.trim())
        .map(field => field.label);
};

const getScaleChoices = (field: SurveyFieldDefinitionData): number[] => {
    const min = field.scaleMin ?? 1;
    const max = field.scaleMax ?? 10;
    return Array.from({ length: Math.max(max - min + 1, 0) }, (_, index) => min + index);
};

// ─── Field Input ─────────────────────────────────────────────────────────────

interface SurveyFieldInputProps {
    field: SurveyFieldDefinitionData;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

const SurveyFieldInput: React.FC<SurveyFieldInputProps> = ({ field, value, onChange, disabled }) => {
    switch (field.type) {
        case SurveyFieldType.SingleLineText:
        case SurveyFieldType.MultilineText:
            return (
                <TextInputField
                    type={field.type === SurveyFieldType.MultilineText ? 'textarea' : 'text'}
                    rows={field.type === SurveyFieldType.MultilineText ? 4 : undefined}
                    value={value}
                    onChange={nextValue => onChange(nextValue)}
                    disabled={disabled}
                />
            );
        case SurveyFieldType.IntNumber:
            return (
                <TextInputField
                    type="number"
                    step="1"
                    value={value}
                    onChange={nextValue => onChange(nextValue)}
                    disabled={disabled}
                />
            );
        case SurveyFieldType.MultiOptions:
            return (
                <ChoiceList role="radiogroup" aria-label={field.label ?? ''}>
                    {(field.options ?? []).map(option => (
                        <ChoiceButton
                            key={option}
                            type="button"
                            role="radio"
                            aria-checked={value === option}
                            $selected={value === option}
                            onClick={() => onChange(option)}
                            disabled={disabled}
                        >
                            <ChoiceIndicator $selected={value === option} />
                            <span>{option}</span>
                        </ChoiceButton>
                    ))}
                </ChoiceList>
            );
        case SurveyFieldType.Scale: {
            const min = field.scaleMin ?? 1;
            const scaleChoices = getScaleChoices(field);
            const renderChoicesAsButtons = scaleChoices.length > 0 && scaleChoices.length <= 10;

            return renderChoicesAsButtons ? (
                <ScaleChoiceGrid role="radiogroup" aria-label={field.label ?? ''}>
                    {scaleChoices.map(choice => (
                        <ScaleChoiceButton
                            key={choice}
                            type="button"
                            role="radio"
                            aria-checked={(value || min.toString()) === choice.toString()}
                            aria-label={`${choice} / ${field.scaleMax ?? 10}`}
                            $selected={(value || min.toString()) === choice.toString()}
                            onClick={() => onChange(choice.toString())}
                            disabled={disabled}
                        >
                            {choice}
                        </ScaleChoiceButton>
                    ))}
                </ScaleChoiceGrid>
            ) : (
                <ScaleFallback>
                    <ScaleLabel>{field.scaleMin ?? 1}</ScaleLabel>
                    <StyledSlider
                        type="range"
                        min={min}
                        max={field.scaleMax ?? 10}
                        value={value || min.toString()}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                        disabled={disabled}
                        style={{
                            '--fill-pct': `${((Number(value || min) - min) / Math.max((field.scaleMax ?? 10) - min, 1)) * 100}%`,
                        } as React.CSSProperties}
                    />
                    <ScaleLabel>{field.scaleMax ?? 10}</ScaleLabel>
                    <ScaleValue>{value || min}</ScaleValue>
                </ScaleFallback>
            );
        }
        case SurveyFieldType.Date:
            return (
                <TextInputField
                    type="date"
                    value={value}
                    onChange={nextValue => onChange(nextValue)}
                    disabled={disabled}
                />
            );
        default:
            return (
                <TextInputField
                    type="text"
                    value={value}
                    onChange={nextValue => onChange(nextValue)}
                    disabled={disabled}
                />
            );
    }
};

// ─── Main Component ───────────────────────────────────────────────────────────

export interface SurveyFormViewProps {
    tokenData: SurveyTokenData;
    /** Provide the JWT token to enable submission. Omit for preview mode (submit disabled). */
    token?: string;
}

export const SurveyFormView: React.FC<SurveyFormViewProps> = ({ tokenData, token }) => {
    const t = useTranslations();
    const isPreview = !token;

    const fieldHints = useMemo<Partial<Record<SurveyFieldType, string>>>(() => ({
        [SurveyFieldType.Scale]: t(TK.surveyHintScale),
        [SurveyFieldType.MultiOptions]: t(TK.surveyHintMultiOptions),
        [SurveyFieldType.MultilineText]: t(TK.surveyHintText),
        [SurveyFieldType.Date]: t(TK.surveyHintDate),
        [SurveyFieldType.IntNumber]: t(TK.surveyHintNumber),
    }), [t]);

    const [values, setValues] = useState<Record<string, string>>(getInitialValues(tokenData));
    const [submitAnonymously, setSubmitAnonymously] = useState(false);
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const requiresContact = tokenData.survey.autoCreateClientIfNotExists === true && !tokenData.clientName?.trim();

    const handleFieldChange = useCallback((fieldKey: string, value: string) => {
        setValues(prev => ({ ...prev, [fieldKey]: value }));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isPreview) return;

        const missingMandatory = getMissingMandatoryFields(tokenData.survey.fields, values);
        if (missingMandatory.length > 0) {
            setError(t(TK.surveyMissingRequiredFields, missingMandatory.join(', ')));
            return;
        }

        if (requiresContact && !contactName.trim()) {
            setError(t(TK.surveyMissingRequiredFields, t(TK.contactName)));
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await postSurveySubmitAsync(
                token,
                values,
                requiresContact ? false : submitAnonymously,
                requiresContact
                    ? {
                        contactName: contactName.trim(),
                        contactEmail: contactEmail.trim() || undefined,
                        contactPhone: contactPhone.trim() || undefined,
                    }
                    : undefined,
            );
            setSuccess(true);
        } catch (err: unknown) {
            setError(getErrorMessage(err) || t(TK.somethingWentWrong));
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <Page>
                <PageShell>
                    <CenterState>
                        <CenterStateInner>
                            <SuccessIcon>✓</SuccessIcon>
                            <SectionTitle style={{ marginTop: 0 }}>{t(TK.surveySubmitted)}</SectionTitle>
                            <SectionText>{t(TK.surveySubmittedBody)}</SectionText>
                        </CenterStateInner>
                    </CenterState>
                </PageShell>
            </Page>
        );
    }

    const disabled = tokenData.alreadyResponded || isPreview;
    const surveyLink = typeof window === 'undefined' ? undefined : window.location.href;
    const resolvedSubtitle = resolveSurveyDescription(
        tokenData,
        tokenData.clientName
            ? t(TK.surveyWelcomeWithName, tokenData.clientName)
            : t(TK.surveyWelcomeGeneric),
        isPreview,
        surveyLink,
    );

    return (
        <Page>
            <PageShell>
                {isPreview && (
                    <BannerAlert color="warning" style={{ borderRadius: '16px', margin: 0 }}>
                        {t(TK.surveyPreviewBanner)}
                    </BannerAlert>
                )}

                <HeroCard>
                    <HeroInner>
                        <HeroCopy>
                            <HeroBadge>{t(TK.survey)}</HeroBadge>
                            <div>
                                <CompanyHeader>
                                    {tokenData.companyImage && (
                                        <CompanyLogo
                                            src={tokenData.companyImage}
                                            alt={tokenData.companyName || 'Appointmint'}
                                        />
                                    )}
                                    <CompanyName>{tokenData.companyName || 'Appointmint'}</CompanyName>
                                </CompanyHeader>
                                <Title>{tokenData.survey.name}</Title>
                            </div>
                            {resolvedSubtitle.startsWith('<')
                                ? <Subtitle dangerouslySetInnerHTML={{ __html: resolvedSubtitle }} />
                                : <Subtitle>{resolvedSubtitle}</Subtitle>
                            }
                            <HeroMetaGrid>
                                <HeroMetaCard>
                                    <HeroMetaLabel>{t(TK.surveyFields)}</HeroMetaLabel>
                                    <HeroMetaValue>{tokenData.survey.fields.length}</HeroMetaValue>
                                </HeroMetaCard>
                                <HeroMetaCard>
                                    <HeroMetaLabel>{t(TK.mandatory)}</HeroMetaLabel>
                                    <HeroMetaValue>{tokenData.survey.fields.filter(f => f.isMandatory).length}</HeroMetaValue>
                                </HeroMetaCard>
                                <HeroMetaCard>
                                    <HeroMetaLabel>{t(TK.surveyResponse)}</HeroMetaLabel>
                                    <HeroMetaValue>{tokenData.alreadyResponded ? t(TK.surveyStatusCompleted) : t(TK.surveyStatusOpen)}</HeroMetaValue>
                                </HeroMetaCard>
                            </HeroMetaGrid>
                        </HeroCopy>

                        <HeroSidePanel>
                            <HeroSideCard>
                                <SideTitle>{t(TK.surveyHonestFeedbackTitle)}</SideTitle>
                                <SideText>{t(TK.surveyHonestFeedbackBody)}</SideText>
                            </HeroSideCard>
                            <HeroSideCard>
                                <SideTitle>{t(TK.surveyPrivacyOptions)}</SideTitle>
                                <SideText>{t(TK.surveyAnonymousHelp)}</SideText>
                            </HeroSideCard>
                        </HeroSidePanel>
                    </HeroInner>
                </HeroCard>

                <ContentCard>
                    <ContentInner>
                        {tokenData.alreadyResponded && (
                            <BannerAlert color="info">
                                {t(TK.surveyAlreadyCompleted)}
                            </BannerAlert>
                        )}

                        <SectionTitle>{t(TK.surveySharePerspective)}</SectionTitle>
                        <SectionText>{t(TK.surveyFormInstructions)}</SectionText>

                        <Form onSubmit={handleSubmit}>
                            <QuestionList>
                                {requiresContact && (
                                    <QuestionCard>
                                        <QuestionHeader>
                                            <QuestionNumber>i</QuestionNumber>
                                            <div>
                                                <QuestionTitle>
                                                    {t(TK.contactName)}
                                                    <MandatoryBadge aria-label={t(TK.mandatory)}>*</MandatoryBadge>
                                                </QuestionTitle>
                                                <QuestionHint>{t(TK.surveyAutoCreateClientHelp)}</QuestionHint>
                                            </div>
                                        </QuestionHeader>

                                        <FormGroup style={{ marginBottom: 'var(--spacer-S)' }}>
                                            <TextInputField
                                                label={t(TK.contactName)}
                                                value={contactName}
                                                onChange={setContactName}
                                                disabled={disabled}
                                            />
                                        </FormGroup>
                                        <FormGroup style={{ marginBottom: 'var(--spacer-S)' }}>
                                            <TextInputField
                                                label={t(TK.contactEmail)}
                                                value={contactEmail}
                                                onChange={setContactEmail}
                                                disabled={disabled}
                                            />
                                        </FormGroup>
                                        <FormGroup style={{ marginBottom: 0 }}>
                                            <TextInputField
                                                label={t(TK.contactPhone)}
                                                value={contactPhone}
                                                onChange={setContactPhone}
                                                disabled={disabled}
                                            />
                                        </FormGroup>
                                    </QuestionCard>
                                )}

                                {tokenData.survey.fields.map((field, index) => (
                                    <QuestionCard key={index}>
                                        <QuestionHeader>
                                            <QuestionNumber>{index + 1}</QuestionNumber>
                                            <div>
                                                <QuestionTitle>
                                                    {field.label}
                                                    {field.isMandatory && <MandatoryBadge aria-label={t(TK.mandatory)}>*</MandatoryBadge>}
                                                </QuestionTitle>
                                                {fieldHints[field.type] && <QuestionHint>{fieldHints[field.type]}</QuestionHint>}
                                            </div>
                                        </QuestionHeader>

                                        <FormGroup style={{ marginBottom: 0 }}>
                                            <SurveyFieldInput
                                                field={field}
                                                value={values[index.toString()] ?? values[field.label] ?? ''}
                                                onChange={value => handleFieldChange(index.toString(), value)}
                                                disabled={disabled}
                                            />
                                        </FormGroup>
                                    </QuestionCard>
                                ))}
                            </QuestionList>

                            {!disabled && !requiresContact && (
                                <AnonymousSection>
                                    <AnonymousTitle>{t(TK.surveyPrivacyOptions)}</AnonymousTitle>
                                    <CheckInputField
                                        value={submitAnonymously}
                                        onChange={setSubmitAnonymously}
                                        label={t(TK.surveySubmitAnonymously)}
                                    />
                                    <AnonymousDescription>
                                        {t(TK.surveyAnonymousHelp)}
                                    </AnonymousDescription>
                                </AnonymousSection>
                            )}

                            {error && (
                                <BannerAlert color="danger" style={{ marginTop: 'var(--spacer-S)' }}>
                                    {error}
                                </BannerAlert>
                            )}

                            {!disabled && <TurnstileWidget />}

                            <FormFooter>
                                <FooterText>
                                    {tokenData.alreadyResponded
                                        ? t(TK.surveyAlreadyCompletedFooter)
                                        : isPreview
                                            ? t(TK.surveyPreviewBanner)
                                            : t(TK.surveyReviewAndSubmit)}
                                </FooterText>
                                <SubmitButton
                                    type="submit"
                                    color="primary"
                                    size="lg"
                                    disabled={submitting || disabled}
                                >
                                    {submitting ? <Spinner size="sm" /> : t(TK.submit)}
                                </SubmitButton>
                            </FormFooter>
                        </Form>
                    </ContentInner>
                </ContentCard>
            </PageShell>
        </Page>
    );
};
